import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MapCanvas from '../components/admin/MapCanvas';
import BotDamagePanel from '../components/BotDamagePanel';
import RadarPanel from '../components/RadarPanel';
import ElectronicWarfareTerminalModal from '../components/ElectronicWarfareTerminalModal';
import BotInventory from './BotInventory';
import { getRadarCapabilities, getRadarSource, isEntityBuried } from '../components/radarUtils';
import { getBatteryMoveTickMs, getAccessorySource, ACCESSORY_ITEM_TYPES } from '../data/accessories';
import { getTilesetConfig } from '../data/tilesets';
import { CITY_TYPES } from './city/cityUtils';
import { loadCityContext } from './city/cityContextCache';
import api from '../services/api';
import { useStore } from '../stores/useStore';

const BASE_MOVE_TICK_MS = 180;
const BLOCKED_TERRAIN_FALLBACK = new Set([2, 3, 4, 5, 7, 8, 11, 13, 14]);
const KEY_TO_DIRECTION = {
  ArrowUp: 'up',
  KeyW: 'up',
  ArrowDown: 'down',
  KeyS: 'down',
  ArrowLeft: 'left',
  KeyA: 'left',
  ArrowRight: 'right',
  KeyD: 'right'
};

function getXY(coordinate, width) {
  return {
    x: coordinate % width,
    y: Math.floor(coordinate / width)
  };
}

function isAdjacentOrSame(fromCoordinate, toCoordinate, width) {
  const from = getXY(Number(fromCoordinate), width);
  const to = getXY(Number(toCoordinate), width);
  return Math.abs(from.x - to.x) + Math.abs(from.y - to.y) <= 1;
}

export default function Game() {
  const navigate = useNavigate();
  const { setRobot, user } = useStore();

  const [loading, setLoading] = useState(true);
  const [robot, setRobotState] = useState(null);
  const [sector, setSector] = useState(null);
  const [tiles, setTiles] = useState([]);
  const [enemies, setEnemies] = useState([]);
  const [cities, setCities] = useState([]);
  const [portals, setPortals] = useState([]);
  const [objects, setObjects] = useState([]);
  const [missionRadarTarget, setMissionRadarTarget] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryAccessories, setInventoryAccessories] = useState({});
  const [tilesetConfigData, setTilesetConfigData] = useState(null);
  const [error, setError] = useState('');
  const [visualCoordinate, setVisualCoordinate] = useState(0);
  const [cityPrompt, setCityPrompt] = useState(null);
  const [cityInfoPrompt, setCityInfoPrompt] = useState(null);
  const [missionPrompt, setMissionPrompt] = useState(null);
  const [objectPrompt, setObjectPrompt] = useState(null);
  const [portalPrompt, setPortalPrompt] = useState(null);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showTerminalModal, setShowTerminalModal] = useState(false);
  const [jumpMode, setJumpMode] = useState(false);
  const [jumpCooldownUntil, setJumpCooldownUntil] = useState(0);
  const [objectActionLoading, setObjectActionLoading] = useState(false);
  const [portalActionLoading, setPortalActionLoading] = useState(false);
  const [showRadarPanel, setShowRadarPanel] = useState(true);
  const [showDamagePanel, setShowDamagePanel] = useState(true);

  const width = sector?.width || 20;
  const height = sector?.height || 15;
  const tilesetConfig = useMemo(
    () => tilesetConfigData || getTilesetConfig(sector?.tileset_id || 'overworld_v1'),
    [sector?.tileset_id, tilesetConfigData]
  );
  const canReturnToDashboard = Boolean(user?.isAdmin);
  const terrainById = useMemo(() => {
    const map = new Map();
    (tilesetConfig?.terrains || []).forEach((terrain) => {
      map.set(Number(terrain.terrainId), terrain);
    });
    return map;
  }, [tilesetConfig]);
  const activeAccessoryRows = useMemo(
    () => Object.values(inventoryAccessories || {}).filter(Boolean),
    [inventoryAccessories]
  );
  const radarSource = useMemo(() => getRadarSource(activeAccessoryRows), [activeAccessoryRows]);
  const radarCapabilities = useMemo(() => getRadarCapabilities(radarSource), [radarSource]);
  const canStartCombat = Boolean(radarSource);
  const batterySource = useMemo(
    () => getAccessorySource(activeAccessoryRows, ACCESSORY_ITEM_TYPES.battery),
    [activeAccessoryRows]
  );
  const ewSource = useMemo(
    () => getAccessorySource(activeAccessoryRows, ACCESSORY_ITEM_TYPES.ewar),
    [activeAccessoryRows]
  );
  const moveTickMs = useMemo(
    () => getBatteryMoveTickMs(batterySource) || BASE_MOVE_TICK_MS,
    [batterySource]
  );
  const hasOperationalLegs = useMemo(() => (
    Number(robot?.v_leg_l || 0) > 0 && Number(robot?.v_leg_r || 0) > 0
  ), [robot?.v_leg_l, robot?.v_leg_r]);
  const jumpCooldownRemainingMs = useMemo(() => (
    Math.max(0, Number(jumpCooldownUntil || 0) - Date.now())
  ), [jumpCooldownUntil]);
  const currentMapObjects = useMemo(() => {
    if (!robot) return [];
    return (objects || []).filter((entry) => Number(entry.coordinate) === Number(robot.coordinate));
  }, [objects, robot]);
  const mapEvents = useMemo(() => {
    return (tiles || [])
      .filter((tile) => Number(tile.layer) === 2 && Number(tile.id_event || 0) > 0)
      .map((tile) => ({
        ...tile,
        coordinate: Number(tile.coordinate),
        id_event: Number(tile.id_event),
        layer: Number(tile.layer)
      }));
  }, [tiles]);
  const detectedCurrentObjects = useMemo(() => {
    if (!currentMapObjects.length || !radarSource) return [];

    return currentMapObjects.filter((entry) => {
      const buried = isEntityBuried(entry);
      if (buried && !radarCapabilities.buriedDetection) return false;
      if (!buried && !radarCapabilities.surfaceDetection) return false;
      return true;
    });
  }, [currentMapObjects, radarCapabilities.buriedDetection, radarCapabilities.surfaceDetection, radarSource]);
  const objectPromptObjects = useMemo(() => {
    if (!objectPrompt) return [];
    if (Array.isArray(objectPrompt.objects) && objectPrompt.objects.length > 0) {
      return objectPrompt.objects;
    }
    return objectPrompt.object ? [objectPrompt.object] : [];
  }, [objectPrompt]);
  const selectedObjectId = objectPrompt?.selectedObjectId || objectPromptObjects[0]?.id || null;
  const selectedObject = useMemo(() => (
    objectPromptObjects.find((entry) => Number(entry.id) === Number(selectedObjectId)) || objectPromptObjects[0] || null
  ), [objectPromptObjects, selectedObjectId]);
  const selectedObjectIsBuried = selectedObject ? isEntityBuried(selectedObject) : Boolean(objectPrompt?.isBuried);

  const movementDirectionRef = useRef(null);
  const moveLockRef = useRef(false);
  const visualCoordinateRef = useRef(0);
  const cityPromptRef = useRef(null);
  const cityInfoPromptRef = useRef(null);
  const objectPromptRef = useRef(null);
  const portalPromptRef = useRef(null);
  const objectDismissedKeyRef = useRef('');
  const portalDismissedKeyRef = useRef('');
  const lastRobotCoordinateRef = useRef(null);

  const viewport = useMemo(() => ({
    width: 13,
    height: 9,
    centerCoordinate: visualCoordinate
  }), [visualCoordinate]);

  const renderTileSize = 64;

  const loadGameState = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const requestStamp = Date.now();
      const botRes = await api.get(`/robots/my-bot?t=${requestStamp}`);
      const botData = botRes.data.robot;
      const [sectorRes, inventoryRes, missionRadarRes] = await Promise.all([
        api.get(`/maps/${botData.id_sector}?t=${requestStamp}`),
        api.get(`/robots/my-inventory?t=${requestStamp}`).catch(() => null),
        api.get(`/missions/radar-target?t=${requestStamp}`).catch(() => null)
      ]);

      setRobotState(botData);
      setRobot(botData);
      setSector(sectorRes.data.sector);
      setTiles(sectorRes.data.tiles || []);
      setEnemies((sectorRes.data.enemies || []).filter((enemy) => Number(enemy.is_alive) === 1));
      setCities(sectorRes.data.cities || []);
      setPortals(sectorRes.data.portals || []);
      setObjects(sectorRes.data.objects || []);
      const rawMissionTarget = missionRadarRes?.data?.target || null;
      setMissionRadarTarget(
        rawMissionTarget && Number(rawMissionTarget.sectorId || 0) === Number(botData.id_sector || 0)
          ? rawMissionTarget
          : null
      );
      setInventoryItems(inventoryRes?.data?.items || []);
      setInventoryAccessories(inventoryRes?.data?.accessories || {});
      setTilesetConfigData(sectorRes.data.tilesetConfig || null);
      setVisualCoordinate(Number(botData.coordinate || 0));
      visualCoordinateRef.current = Number(botData.coordinate || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar el estado del juego');
    } finally {
      setLoading(false);
    }
  }, [setRobot]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const cityContext = await loadCityContext({ force: true }).catch(() => null);
      if (cancelled) return;
      if (cityContext?.transport) {
        navigate('/city/transport', { replace: true });
        return;
      }

      await loadGameState();
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [loadGameState, navigate]);

  useEffect(() => {
    visualCoordinateRef.current = visualCoordinate;
  }, [visualCoordinate]);

  useEffect(() => {
    if (robot?.coordinate === undefined || robot?.coordinate === null) return;
    setVisualCoordinate(Number(robot.coordinate));
  }, [robot?.coordinate]);

  useEffect(() => {
    const currentCoordinate = Number(robot?.coordinate ?? 0);
    if (lastRobotCoordinateRef.current !== null && lastRobotCoordinateRef.current !== currentCoordinate) {
      objectDismissedKeyRef.current = '';
      portalDismissedKeyRef.current = '';
    }
    lastRobotCoordinateRef.current = currentCoordinate;
  }, [robot?.coordinate]);

  const getTerrainType = useCallback((coordinate) => {
    const tile = tiles.find((entry) => Number(entry.coordinate) === Number(coordinate) && Number(entry.layer) === 0);
    return Number(tile?.terrain_type ?? 0);
  }, [tiles]);

  const canStandOnTerrain = useCallback((terrainType) => {
    const terrain = terrainById.get(Number(terrainType));
    if (terrain) {
      return terrain.walkable !== false;
    }
    return !BLOCKED_TERRAIN_FALLBACK.has(Number(terrainType));
  }, [terrainById]);

  const getEnemyAtCoordinate = useCallback((coordinate) => (
    enemies.find((enemy) => Number(enemy.coordinate) === Number(coordinate)) || null
  ), [enemies]);
  const getMineAtCoordinate = useCallback((coordinate) => (
    (objects || []).find((entry) => (
      Number(entry.coordinate) === Number(coordinate) &&
      (Boolean(entry.is_mine) || Number(entry.item_type || 0) === 7)
    )) || null
  ), [objects]);

  const getNextCoordinate = useCallback((fromCoordinate, direction) => {
    const { x, y } = getXY(fromCoordinate, width);

    if (direction === 'up') {
      if (y <= 0) return null;
      return (y - 1) * width + x;
    }

    if (direction === 'down') {
      if (y >= height - 1) return null;
      return (y + 1) * width + x;
    }

    if (direction === 'left') {
      if (x <= 0) return null;
      return y * width + (x - 1);
    }

    if (direction === 'right') {
      if (x >= width - 1) return null;
      return y * width + (x + 1);
    }

    return null;
  }, [height, width]);

  const getMovePreview = useCallback((coordinate) => {
    const terrainType = getTerrainType(coordinate);
    const enemy = getEnemyAtCoordinate(coordinate);

    if (!canStandOnTerrain(terrainType)) {
      return { valid: false, blocked: true, reason: 'terrain' };
    }

    if (enemy) {
      return {
        valid: false,
        blocked: true,
        reason: 'enemy',
        enemyInstanceId: Number(enemy.id || enemy.id_instance || enemy.id_enemy_instance || 0)
      };
    }

    return { valid: true, blocked: false, reason: 'free' };
  }, [canStandOnTerrain, getEnemyAtCoordinate, getTerrainType]);
  const getJumpPreview = useCallback((originCoordinate, direction) => {
    const middleCoordinate = getNextCoordinate(originCoordinate, direction);
    if (middleCoordinate === null) {
      return { valid: false, reason: 'bounds', coordinate: null, middleCoordinate: null };
    }

    const destinationCoordinate = getNextCoordinate(middleCoordinate, direction);
    if (destinationCoordinate === null) {
      return { valid: false, reason: 'bounds', coordinate: null, middleCoordinate };
    }

    const middleEnemy = getEnemyAtCoordinate(middleCoordinate);
    if (!middleEnemy) {
      return { valid: false, reason: 'no-enemy', coordinate: destinationCoordinate, middleCoordinate };
    }

    const terrainType = getTerrainType(destinationCoordinate);
    if (!canStandOnTerrain(terrainType)) {
      return { valid: false, reason: 'terrain', coordinate: destinationCoordinate, middleCoordinate };
    }

    if (getEnemyAtCoordinate(destinationCoordinate)) {
      return { valid: false, reason: 'enemy', coordinate: destinationCoordinate, middleCoordinate };
    }

    if (getMineAtCoordinate(destinationCoordinate)) {
      return { valid: false, reason: 'mine', coordinate: destinationCoordinate, middleCoordinate };
    }

    return {
      valid: true,
      reason: 'jump',
      coordinate: destinationCoordinate,
      middleCoordinate,
      enemyInstanceId: Number(middleEnemy.id || middleEnemy.id_instance || middleEnemy.id_enemy_instance || 0)
    };
  }, [canStandOnTerrain, getEnemyAtCoordinate, getMineAtCoordinate, getNextCoordinate, getTerrainType]);

  const movementPreview = useMemo(() => {
    const origin = Number(visualCoordinate || 0);
    const steps = [
      { direction: 'up', offsetX: 0, offsetY: -1 },
      { direction: 'down', offsetX: 0, offsetY: 1 },
      { direction: 'left', offsetX: -1, offsetY: 0 },
      { direction: 'right', offsetX: 1, offsetY: 0 }
    ];

    return steps.map((step) => {
      const { x, y } = getXY(origin, width);
      const nx = x + step.offsetX;
      const ny = y + step.offsetY;

      if (nx < 0 || ny < 0 || nx >= width || ny >= height) {
        return { direction: step.direction, coordinate: null, valid: false };
      }

      const coordinate = ny * width + nx;
      const preview = getMovePreview(coordinate);
      return {
        direction: step.direction,
        coordinate,
        valid: preview.valid,
        blocked: preview.blocked,
        reason: preview.reason,
        enemyInstanceId: preview.enemyInstanceId || null
      };
    });
  }, [getMovePreview, height, visualCoordinate, width]);
  const availableJumpPreview = useMemo(() => {
    if (!hasOperationalLegs) return [];

    return ['up', 'down', 'left', 'right']
      .map((direction) => {
        const preview = getJumpPreview(Number(visualCoordinate || 0), direction);
        if (!preview.valid || preview.coordinate === null) return null;
        return {
          direction,
          coordinate: preview.coordinate,
          valid: true,
          blocked: false,
          reason: preview.reason,
          middleCoordinate: preview.middleCoordinate,
          enemyInstanceId: preview.enemyInstanceId || null,
          shape: 'box',
          label: 'S'
        };
      })
      .filter(Boolean);
  }, [getJumpPreview, hasOperationalLegs, visualCoordinate]);
  const jumpPreview = useMemo(() => (
    jumpMode ? availableJumpPreview : []
  ), [availableJumpPreview, jumpMode]);
  const canUseJump = hasOperationalLegs && jumpCooldownRemainingMs <= 0 && availableJumpPreview.length > 0;

  const cancelCityEntry = useCallback(async ({ persist = true } = {}) => {
    const prompt = cityPromptRef.current;
    if (!prompt) return;

    movementDirectionRef.current = null;
    setShowTerminalModal(false);
    setCityPrompt(null);
    cityPromptRef.current = null;
    setRobotState(prompt.previousRobot);
    setRobot(prompt.previousRobot);
    setVisualCoordinate(Number(prompt.previousRobot.coordinate || 0));
    visualCoordinateRef.current = Number(prompt.previousRobot.coordinate || 0);
    moveLockRef.current = false;

    if (persist) {
      void api.post('/robots/cancel-city-entry', {
        sectorId: prompt.sectorId,
        coordinate: prompt.coordinate
      }).catch(() => {
        // Limpieza best-effort.
      });
    }
  }, [setRobot]);

  const closeObjectPrompt = useCallback(() => {
    if (objectPromptRef.current?.key) {
      objectDismissedKeyRef.current = objectPromptRef.current.key;
    }
    objectPromptRef.current = null;
    setObjectPrompt(null);
  }, []);

  const closePortalPrompt = useCallback(() => {
    if (portalPromptRef.current?.key) {
      portalDismissedKeyRef.current = portalPromptRef.current.key;
    }
    portalPromptRef.current = null;
    setPortalPrompt(null);
  }, []);

  const openInventoryModal = useCallback(() => {
    setShowInventoryModal(true);
  }, []);

  const closeInventoryModal = useCallback(() => {
    setShowInventoryModal(false);
  }, []);

  const closeCityInfoPrompt = useCallback(() => {
    cityInfoPromptRef.current = null;
    setCityInfoPrompt(null);
  }, []);

  const closeMissionPrompt = useCallback(() => {
    setMissionPrompt(null);
  }, []);

  const handleMapCityClick = useCallback((coordinate) => {
    const city = (cities || []).find((entry) => Number(entry.coordinate) === Number(coordinate));
    if (!city) {
      closeCityInfoPrompt();
      return;
    }

    const botClanId = Number(robot?.clan || 0);
    const cityClanId = Number(city.clan || 0);
    const isOwnClan = botClanId > 0 && cityClanId > 0 && botClanId === cityClanId;
    const nextPrompt = {
      key: `city-info:${Number(city.id || city.coordinate || coordinate)}`,
      coordinate: Number(coordinate),
      city,
      isOwnClan
    };

    cityInfoPromptRef.current = nextPrompt;
    setCityInfoPrompt(nextPrompt);
  }, [cities, closeCityInfoPrompt, robot?.clan]);

  const openTerminalModal = useCallback(() => {
    if (!ewSource) return;
    setShowTerminalModal(true);
  }, [ewSource]);

  const closeTerminalModal = useCallback(() => {
    setShowTerminalModal(false);
  }, []);

  const handleCityBypassAuthorized = useCallback((payload) => {
    setCityPrompt((current) => {
      if (!current) return current;
      const nextPrompt = {
        ...current,
        access: {
          ...current.access,
          ...(payload?.cityAccess || {}),
          requiresTerminalBypass: false,
          terminalBypassAuthorized: true,
          entryFeeRequired: false,
          entryFeeAmount: 0
        },
        message: payload?.message || 'Acceso a ciudad hackeado. Ya puedes confirmar la entrada.'
      };
      cityPromptRef.current = nextPrompt;
      return nextPrompt;
    });
    setError('');
    setShowTerminalModal(false);
  }, []);

  const handlePickupObject = useCallback(async () => {
    const prompt = objectPromptRef.current;
    const promptObjects = Array.isArray(prompt?.objects) && prompt.objects.length > 0
      ? prompt.objects
      : (prompt?.object ? [prompt.object] : []);
    const promptSelectedId = prompt?.selectedObjectId || promptObjects[0]?.id || null;
    const promptSelected = promptObjects.find((entry) => Number(entry.id) === Number(promptSelectedId)) || promptObjects[0] || null;
    if (!prompt || !promptSelected || !robot || objectActionLoading) return;

    setObjectActionLoading(true);
    setError('');
    try {
      await api.post('/robots/map-object/pickup', {
        sectorId: robot.id_sector,
        coordinate: prompt.coordinate,
        objectId: promptSelected.id,
        source: promptSelected.source || (Number(promptSelected.item_type || 0) === 7 ? 'mine' : 'ground')
      });
      objectDismissedKeyRef.current = '';
      objectPromptRef.current = null;
      setObjectPrompt(null);
      await loadGameState();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo recoger el objeto');
    } finally {
      setObjectActionLoading(false);
    }
  }, [loadGameState, objectActionLoading, robot]);

  const handleDisarmMine = useCallback(async () => {
    const prompt = objectPromptRef.current;
    const promptObjects = Array.isArray(prompt?.objects) && prompt.objects.length > 0
      ? prompt.objects
      : (prompt?.object ? [prompt.object] : []);
    const promptSelectedId = prompt?.selectedObjectId || promptObjects[0]?.id || null;
    const promptSelected = promptObjects.find((entry) => Number(entry.id) === Number(promptSelectedId)) || promptObjects[0] || null;
    if (!prompt || !promptSelected || !robot || objectActionLoading) return;

    setObjectActionLoading(true);
    setError('');
    try {
      await api.post('/robots/map-object/disarm', {
        sectorId: robot.id_sector,
        coordinate: prompt.coordinate,
        objectId: promptSelected.id
      });
      objectDismissedKeyRef.current = '';
      objectPromptRef.current = null;
      setObjectPrompt(null);
      await loadGameState();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo desactivar la mina');
    } finally {
      setObjectActionLoading(false);
    }
  }, [loadGameState, objectActionLoading, robot]);

  const handleStartCombat = useCallback(async () => {
    const prompt = objectPromptRef.current;
    if (!prompt || !robot || objectActionLoading) return;
    if (!radarSource) {
      setError('Necesitas un radar equipado para iniciar combate');
      return;
    }

    setObjectActionLoading(true);
    setError('');
    try {
      const res = await api.post('/combat/start', {
        enemyInstanceId: prompt.enemyInstanceId || prompt.object?.id
      });
      objectPromptRef.current = null;
      setObjectPrompt(null);
      setError(`Combate iniciado contra ${res.data.combat?.enemyName || prompt.object?.name || 'enemigo'}.`);
      navigate('/combat', {
        replace: true,
        state: {
          combatStarted: true,
          combat: res.data.combat || null
        }
      });
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo iniciar el combate');
    } finally {
      setObjectActionLoading(false);
    }
  }, [navigate, objectActionLoading, radarSource, robot]);

  const handleUsePortal = useCallback(async () => {
    const prompt = portalPromptRef.current;
    if (!prompt || !robot || portalActionLoading) return;

    setPortalActionLoading(true);
    setError('');

    try {
      const res = await api.post('/robots/use-portal', {
        sectorId: Number(robot.id_sector || 0),
        coordinate: Number(prompt.coordinate || 0)
      });

      portalDismissedKeyRef.current = '';
      portalPromptRef.current = null;
      setPortalPrompt(null);
      setCityPrompt(null);
      cityPromptRef.current = null;
      setObjectPrompt(null);
      objectPromptRef.current = null;
      setError(`Portal activado. Destino: ${res.data.portal?.destinationSectorName || 'sector desconocido'}.`);
      await loadGameState();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo usar el portal');
    } finally {
      setPortalActionLoading(false);
    }
  }, [loadGameState, portalActionLoading, robot]);

  useEffect(() => {
    if (!cityPrompt) return undefined;

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        void cancelCityEntry();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [cityPrompt, cancelCityEntry]);

  useEffect(() => {
    if (!cityInfoPrompt) return undefined;

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeCityInfoPrompt();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [closeCityInfoPrompt, cityInfoPrompt]);

  useEffect(() => {
    if (!objectPrompt) return undefined;

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeObjectPrompt();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [closeObjectPrompt, objectPrompt]);

  useEffect(() => {
    if (!portalPrompt) return undefined;

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePortalPrompt();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [closePortalPrompt, portalPrompt]);

  useEffect(() => {
    if (!jumpMode) return undefined;

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setJumpMode(false);
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [jumpMode]);

  useEffect(() => {
    if (!robot || cityPrompt) return;

    if (!detectedCurrentObjects.length) {
      if (objectPromptRef.current?.source === 'scan') {
        objectPromptRef.current = null;
        setObjectPrompt(null);
      }
      return;
    }

    const promptKey = `${Number(robot.id_sector || 0)}:${Number(robot.coordinate || 0)}:${detectedCurrentObjects.map((entry) => `${Number(entry.id || entry.id_object || 0)}:${Number(entry.item_type || 0)}:${entry.source || 'unknown'}`).join('|')}`;
    if (objectDismissedKeyRef.current === promptKey) return;
    if (objectPromptRef.current?.key === promptKey) return;

    const nextPrompt = {
      key: promptKey,
      source: 'scan',
      kind: detectedCurrentObjects.length > 1 ? 'multi' : (Number(detectedCurrentObjects[0].item_type || 0) === 7 ? 'mine' : 'object'),
      object: detectedCurrentObjects[0],
      objects: detectedCurrentObjects,
      selectedObjectId: detectedCurrentObjects[0]?.id || detectedCurrentObjects[0]?.id_object || null,
      sectorId: Number(robot.id_sector || 0),
      coordinate: Number(detectedCurrentObjects[0].coordinate || 0),
      isBuried: detectedCurrentObjects.every((entry) => isEntityBuried(entry)),
      canPickup: detectedCurrentObjects.some((entry) => Number(entry.item_type || 0) !== 7),
      canDisarm: detectedCurrentObjects.some((entry) => Number(entry.item_type || 0) === 7)
    };

    objectPromptRef.current = nextPrompt;
    setObjectPrompt(nextPrompt);
  }, [cityPrompt, detectedCurrentObjects, robot, showInventoryModal]);

  useEffect(() => {
    if (!robot || cityPrompt || objectPrompt || showInventoryModal) return;

    const nearbyPortal = (portals || []).find((portal) => (
      isAdjacentOrSame(robot.coordinate, portal.coordenada_origen, width)
    ));

    if (!nearbyPortal) {
      if (portalPromptRef.current) {
        portalPromptRef.current = null;
        setPortalPrompt(null);
      }
      return;
    }

    const promptKey = `${Number(nearbyPortal.id_sector_origen || robot.id_sector)}:${Number(nearbyPortal.coordenada_origen || 0)}:${Number(nearbyPortal.id_sector_destino || 0)}:${Number(nearbyPortal.coordenada_destino || 0)}`;
    if (portalDismissedKeyRef.current === promptKey) return;
    if (portalPromptRef.current?.key === promptKey) return;

    const nextPrompt = {
      key: promptKey,
      coordinate: Number(nearbyPortal.coordenada_origen || 0),
      destinationSectorId: Number(nearbyPortal.id_sector_destino || 0),
      destinationCoordinate: Number(nearbyPortal.coordenada_destino || 0),
      destinationSectorName: nearbyPortal.destination_sector_name || `Sector #${nearbyPortal.id_sector_destino || '-'}`,
      portal: nearbyPortal
    };

    portalPromptRef.current = nextPrompt;
    setPortalPrompt(nextPrompt);
  }, [cityPrompt, objectPrompt, portals, robot, showInventoryModal, width]);

  const showCityBlockedModal = useCallback((city, message, previousRobot, optimisticRobot) => {
    const nextPrompt = {
      sectorId: previousRobot.id_sector,
      coordinate: previousRobot.coordinate,
      city,
      access: { isNonNeutralEnteringForeignClanCity: true },
      message,
      previousRobot,
      optimisticRobot
    };

    movementDirectionRef.current = null;
    setCityPrompt(nextPrompt);
    cityPromptRef.current = nextPrompt;
    setRobotState(previousRobot);
    setRobot(previousRobot);
    setVisualCoordinate(Number(previousRobot.coordinate || 0));
    visualCoordinateRef.current = Number(previousRobot.coordinate || 0);
    moveLockRef.current = false;
  }, [setRobot]);

  const confirmCityEntry = useCallback(async () => {
    const prompt = cityPromptRef.current;
    if (!prompt) return;

    setError('');
    try {
      const res = await api.post('/robots/confirm-city-entry', {
        sectorId: prompt.sectorId,
        coordinate: prompt.coordinate
      });

      const entryFeePaid = Number(res.data.entryFeePaid || 0);
      const confirmedRobot = {
        ...prompt.optimisticRobot,
        coordinate: res.data.coordinate,
        money: entryFeePaid > 0
          ? Math.max(0, Number(prompt.previousRobot.money || 0) - entryFeePaid)
          : prompt.previousRobot.money
      };

      setRobotState(confirmedRobot);
      setRobot(confirmedRobot);
      setVisualCoordinate(Number(res.data.coordinate));
      visualCoordinateRef.current = Number(res.data.coordinate);
      setShowTerminalModal(false);
      setCityPrompt(null);
      cityPromptRef.current = null;
      moveLockRef.current = false;
      navigate('/dashboard');
    } catch (err) {
      const data = err.response?.data;
      setError(data?.error || 'No se pudo confirmar la entrada a la ciudad');
      if (data?.code === 'CITY_HACK_REQUIRED') {
        return;
      }
      await cancelCityEntry({ persist: true });
    }
  }, [cancelCityEntry, navigate, setRobot]);

  const requestMove = useCallback(async (coordinate) => {
    if (!robot || moveLockRef.current) return false;

    moveLockRef.current = true;
    const previousRobot = robot;
    const optimisticRobot = { ...robot, coordinate };

    setError('');
    setRobotState(optimisticRobot);
    setRobot(optimisticRobot);
    setVisualCoordinate(coordinate);

    try {
      const res = await api.post('/robots/move', {
        sectorId: robot.id_sector,
        coordinate
      });

      if (res.data.requiresCityEntryConfirmation && res.data.cityEntered) {
        const nextPrompt = {
          sectorId: robot.id_sector,
          coordinate,
          city: res.data.cityEntered,
          access: res.data.cityAccess || {},
          previousRobot,
          optimisticRobot
        };

        setRobotState(optimisticRobot);
        setRobot(optimisticRobot);
        setVisualCoordinate(coordinate);
        setCityPrompt(nextPrompt);
        cityPromptRef.current = nextPrompt;
        return true;
      }

      const nextRobotState = res.data.botState
        ? {
          ...optimisticRobot,
          ...res.data.botState
        }
        : {
          ...optimisticRobot,
          coordinate: Number(res.data.coordinate)
        };

      setRobotState(nextRobotState);
      setRobot(nextRobotState);
      setVisualCoordinate(Number(nextRobotState.coordinate || res.data.coordinate));
      visualCoordinateRef.current = Number(nextRobotState.coordinate || res.data.coordinate);

      if (res.data.missionUpdate) {
        setMissionPrompt(res.data.missionUpdate);
        await loadGameState();
      }
      return true;
    } catch (err) {
      setRobotState(previousRobot);
      setRobot(previousRobot);
      setVisualCoordinate(Number(previousRobot.coordinate || 0));
      visualCoordinateRef.current = Number(previousRobot.coordinate || 0);

      const data = err.response?.data;
      if (data?.code === 'ENEMY_BLOCKING_TILE' && data.enemyInstanceId) {
        const enemy = enemies.find((entry) => Number(entry.id || 0) === Number(data.enemyInstanceId)) || {
          id: Number(data.enemyInstanceId),
          coordinate,
          name: 'Enemigo detectado'
        };
        const nextPrompt = {
          key: `enemy-block:${Number(enemy.coordinate || coordinate)}:${Number(enemy.id || data.enemyInstanceId)}`,
          source: 'enemy-block',
          kind: 'enemy',
          object: enemy,
          enemyInstanceId: Number(data.enemyInstanceId),
          sectorId: Number(robot.id_sector || 0),
          coordinate: Number(enemy.coordinate || coordinate),
          canFight: canStartCombat,
          canFlee: true
        };

        objectPromptRef.current = nextPrompt;
        setObjectPrompt(nextPrompt);
      } else if (data?.code === 'MINE_BLOCKING_TILE' && data.mine) {
        const nextPrompt = {
          key: `mine-block:${Number(data.mine.coordinate || coordinate)}:${Number(data.mine.id_object || data.mine.id || 0)}`,
          source: 'mine-block',
          kind: 'mine',
          object: data.mine,
          sectorId: Number(robot.id_sector || 0),
          coordinate: Number(data.mine.coordinate || coordinate),
          isBuried: isEntityBuried(data.mine),
          canPickup: false,
          canDisarm: true
        };

        objectPromptRef.current = nextPrompt;
        setObjectPrompt(nextPrompt);
      } else if (data?.code === 'FORBIDDEN_FOREIGN_CITY_ENTRY') {
        const blockedCity = cities.find((city) => Number(city.coordinate) === Number(coordinate)) || {
          coordinate,
          name: 'Ciudad bloqueada',
          city_type: 2,
          clan: null,
          clan_name: null,
          image: null
        };
        setError('');
        showCityBlockedModal(
          blockedCity,
          data?.error || 'Entrada prohibida: esta ciudad pertenece a otro clan',
          previousRobot,
          optimisticRobot
        );
      } else {
        setError(data?.error || 'Movimiento no valido');
      }
      return false;
    } finally {
      if (!cityPromptRef.current) {
        moveLockRef.current = false;
      }
    }
  }, [cities, enemies, loadGameState, robot, setRobot, showCityBlockedModal]);
  const requestJump = useCallback(async (coordinate) => {
    if (!robot || moveLockRef.current) return false;

    moveLockRef.current = true;
    const previousRobot = robot;
    const optimisticRobot = { ...robot, coordinate };

    setError('');
    setJumpMode(false);
    setRobotState(optimisticRobot);
    setRobot(optimisticRobot);
    setVisualCoordinate(coordinate);

    try {
      const res = await api.post('/robots/jump', {
        sectorId: robot.id_sector,
        coordinate
      });

      if (res.data.requiresCityEntryConfirmation && res.data.cityEntered) {
        const nextPrompt = {
          sectorId: robot.id_sector,
          coordinate,
          city: res.data.cityEntered,
          access: res.data.cityAccess || {},
          previousRobot,
          optimisticRobot
        };

        setRobotState(optimisticRobot);
        setRobot(optimisticRobot);
        setVisualCoordinate(coordinate);
        setCityPrompt(nextPrompt);
        cityPromptRef.current = nextPrompt;
        return true;
      }

      const nextRobotState = res.data.botState
        ? {
          ...optimisticRobot,
          ...res.data.botState
        }
        : {
          ...optimisticRobot,
          coordinate: Number(res.data.coordinate)
        };

      setRobotState(nextRobotState);
      setRobot(nextRobotState);
      setVisualCoordinate(Number(nextRobotState.coordinate || res.data.coordinate));
      visualCoordinateRef.current = Number(nextRobotState.coordinate || res.data.coordinate);
      setJumpCooldownUntil(Number(res.data.jumpCooldownUntil || 0));

      if (res.data.missionUpdate) {
        setMissionPrompt(res.data.missionUpdate);
        await loadGameState();
      }
      return true;
    } catch (err) {
      setRobotState(previousRobot);
      setRobot(previousRobot);
      setVisualCoordinate(Number(previousRobot.coordinate || 0));
      visualCoordinateRef.current = Number(previousRobot.coordinate || 0);

      const data = err.response?.data;
      if (data?.code === 'JUMP_COOLDOWN' && data.jumpCooldownUntil) {
        setJumpCooldownUntil(Number(data.jumpCooldownUntil || 0));
        setError(data?.error || 'Los propulsores todavia se estan recargando');
      } else if (data?.code === 'ENEMY_BLOCKING_TILE' && data.enemyInstanceId) {
        const enemy = enemies.find((entry) => Number(entry.id || 0) === Number(data.enemyInstanceId)) || {
          id: Number(data.enemyInstanceId),
          coordinate,
          name: 'Enemigo detectado'
        };
        const nextPrompt = {
          key: `enemy-block:${Number(enemy.coordinate || coordinate)}:${Number(enemy.id || data.enemyInstanceId)}`,
          source: 'enemy-block',
          kind: 'enemy',
          object: enemy,
          enemyInstanceId: Number(data.enemyInstanceId),
          sectorId: Number(robot.id_sector || 0),
          coordinate: Number(enemy.coordinate || coordinate),
          canFight: canStartCombat,
          canFlee: true
        };

        objectPromptRef.current = nextPrompt;
        setObjectPrompt(nextPrompt);
      } else if (data?.code === 'MINE_BLOCKING_TILE' && data.mine) {
        const nextPrompt = {
          key: `mine-block:${Number(data.mine.coordinate || coordinate)}:${Number(data.mine.id_object || data.mine.id || 0)}`,
          source: 'mine-block',
          kind: 'mine',
          object: data.mine,
          sectorId: Number(robot.id_sector || 0),
          coordinate: Number(data.mine.coordinate || coordinate),
          isBuried: isEntityBuried(data.mine),
          canPickup: false,
          canDisarm: true
        };

        objectPromptRef.current = nextPrompt;
        setObjectPrompt(nextPrompt);
      } else if (data?.code === 'FORBIDDEN_FOREIGN_CITY_ENTRY') {
        const blockedCity = cities.find((city) => Number(city.coordinate) === Number(coordinate)) || {
          coordinate,
          name: 'Ciudad bloqueada',
          city_type: 2,
          clan: null,
          clan_name: null,
          image: null
        };
        setError('');
        showCityBlockedModal(
          blockedCity,
          data?.error || 'Entrada prohibida: esta ciudad pertenece a otro clan',
          previousRobot,
          optimisticRobot
        );
      } else {
        setError(data?.error || 'Salto no valido');
      }
      return false;
    } finally {
      if (!cityPromptRef.current) {
        moveLockRef.current = false;
      }
    }
  }, [canStartCombat, cities, enemies, loadGameState, robot, setRobot, showCityBlockedModal]);

  const handleMapCanvasClick = useCallback((coordinate) => {
    if (jumpMode) {
      const selectedJump = jumpPreview.find((entry) => Number(entry.coordinate) === Number(coordinate));
      if (!selectedJump) return;
      void requestJump(coordinate);
      return;
    }

    handleMapCityClick(coordinate);
  }, [handleMapCityClick, jumpMode, jumpPreview, requestJump]);

  const handleToggleJumpMode = useCallback(() => {
    if (jumpMode) {
      setJumpMode(false);
      return;
    }

    if (!hasOperationalLegs) {
      setError('Necesitas ambas piernas operativas para saltar');
      return;
    }

    if (jumpCooldownRemainingMs > 0) {
      setError(`Los propulsores se estan recargando (${Math.ceil(jumpCooldownRemainingMs / 1000)} s)`);
      return;
    }

    if (!availableJumpPreview.length) {
      setError('No hay un salto valido disponible desde esta posicion');
      return;
    }

    setError('');
    setJumpMode(true);
  }, [availableJumpPreview.length, hasOperationalLegs, jumpCooldownRemainingMs, jumpMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const direction = KEY_TO_DIRECTION[event.code];
      if (!direction || !robot) return;

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code)) {
        event.preventDefault();
      }

      movementDirectionRef.current = direction;
    };

    const handleKeyUp = (event) => {
      const direction = KEY_TO_DIRECTION[event.code];
      if (!direction) return;

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code)) {
        event.preventDefault();
      }

      if (movementDirectionRef.current === direction) {
        movementDirectionRef.current = null;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [robot]);

  useEffect(() => {
    if (!robot || !sector || cityPrompt || jumpMode) return undefined;

    const interval = setInterval(() => {
      const direction = movementDirectionRef.current;
      if (!direction || moveLockRef.current || cityPromptRef.current) return;

      const fromCoordinate = visualCoordinateRef.current;
      const nextCoordinate = getNextCoordinate(fromCoordinate, direction);
      if (nextCoordinate === null) return;

      const preview = getMovePreview(nextCoordinate);
      if (!preview.valid && preview.reason !== 'enemy') return;

      requestMove(nextCoordinate);
    }, moveTickMs);

    return () => clearInterval(interval);
  }, [cityPrompt, getMovePreview, getNextCoordinate, jumpMode, moveTickMs, requestMove, robot, sector]);

  if (loading) {
    return (
      <div className="min-h-screen bg-game-dark text-gray-300 flex items-center justify-center">
        Cargando estado del juego...
      </div>
    );
  }

  if (!robot || !sector) {
    return (
      <div className="min-h-screen bg-game-dark text-gray-300 flex flex-col items-center justify-center gap-4">
        <div>{error || 'No tienes robot activo. Crea uno en el panel de admin.'}</div>
        <button className="px-4 py-2 rounded bg-gray-800 border border-gray-700" onClick={() => navigate('/onboarding')}>
          Ir a onboarding
        </button>
      </div>
    );
  }

  const cityTypeLabel = CITY_TYPES[Number(cityPrompt?.city?.city_type ?? -1)] || 'Asentamiento';
  const cityClanLabel = cityPrompt?.city?.clan_name || (Number(cityPrompt?.city?.clan) === 0 ? 'Neutral' : `Clan #${cityPrompt?.city?.clan ?? '-'}`);
  const botClanId = Number(robot?.clan || 0);
  const cityClanId = Number(cityPrompt?.city?.clan || 0);
  const sameClanCity = Boolean(cityPrompt?.city) && botClanId === cityClanId;
  const needsFee = Boolean(cityPrompt?.access?.entryFeeRequired);
  const entryFeeAmount = Number(cityPrompt?.access?.entryFeeAmount || 0);
  const cityHackRequired = Boolean(!sameClanCity && cityPrompt?.access?.requiresTerminalBypass && !cityPrompt?.access?.terminalBypassAuthorized);
  const canEnterCity = cityPrompt ? (sameClanCity || !cityPrompt?.access || (cityPrompt.access.isNonNeutralEnteringForeignClanCity === false && !cityHackRequired)) : false;
  const cityMessage = cityPrompt?.message || (
    cityHackRequired
      ? 'Esta ciudad requiere que actives la terminal EW para hackear la entrada antes de confirmar.'
      : canEnterCity
        ? (needsFee
          ? 'Tu bot ha llegado a una ciudad. Puedes entrar y usar sus servicios.'
          : 'Tu bot ha llegado a una ciudad. Puedes entrar y usar sus servicios.')
        : 'Esta ciudad pertenece a otro clan y no puedes entrar.'
  );

  return (
    <div className="min-h-screen bg-game-dark text-gray-200 p-4">
      <div className="max-w-[1680px] mx-auto">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-gray-400">
              Sector: {sector.name || `#${sector.id}`} | Coordenada bot: {robot.coordinate}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              className={`px-4 py-2 rounded border ${
                jumpMode
                  ? 'bg-amber-500/20 border-amber-300/50 text-amber-100'
                  : canUseJump
                    ? 'bg-violet-600/20 border-violet-400/40 text-violet-100 hover:bg-violet-500/25'
                    : 'bg-gray-900 border-gray-800 text-gray-500 cursor-not-allowed'
              }`}
              onClick={handleToggleJumpMode}
              disabled={!jumpMode && !canUseJump}
            >
              {jumpMode ? 'Cancelar salto' : 'Salto'}
            </button>
            <button
              className="px-4 py-2 rounded bg-cyan-600/20 border border-cyan-400/40 text-cyan-100 hover:bg-cyan-500/25"
              onClick={openInventoryModal}
            >
              Inventario
            </button>
            {canReturnToDashboard && (
              <button
                className="px-4 py-2 rounded bg-gray-900 border border-gray-700 hover:border-gray-500"
                onClick={() => navigate('/dashboard')}
              >
                Volver
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded border border-rose-500/50 bg-rose-500/10 text-rose-300 text-sm">
            {error}
          </div>
        )}

        {jumpMode && (
          <div className="mb-4 p-3 rounded border border-violet-500/40 bg-violet-500/10 text-violet-100 text-sm">
            Modo salto activo. Elige una casilla marcada para saltar por encima del enemigo.
          </div>
        )}

        {missionPrompt && (
          <div className="mb-4 rounded-2xl border border-amber-400/40 bg-amber-500/10 px-4 py-3 shadow-[0_0_30px_rgba(251,191,36,0.12)]">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-[0.25em] text-amber-200/80">Mision</p>
                <h3 className="text-lg font-bold text-amber-50">{missionPrompt.missionName || 'Evento de mision'}</h3>
                <p className="text-sm text-slate-200">
                  Fase {missionPrompt.phaseNumber || 1} de {missionPrompt.totalPhases || 1}
                </p>
                {missionPrompt.eventText && (
                  <p className="text-sm text-slate-300">{missionPrompt.eventText}</p>
                )}
                {Array.isArray(missionPrompt.summary) && missionPrompt.summary.length > 0 && (
                  <p className="text-sm text-amber-100/90">{missionPrompt.summary.join(' | ')}</p>
                )}
              </div>
              <button
                type="button"
                onClick={closeMissionPrompt}
                className="rounded-xl border border-amber-300/40 bg-[#140f05] px-4 py-2 text-sm font-semibold text-amber-100 hover:bg-[#241807]"
              >
                Cerrar
              </button>
            </div>
          </div>
        )}

        <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.12),rgba(4,6,13,0.96)_58%)] p-3 shadow-[0_0_40px_rgba(0,255,255,0.08)]">
          <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(135deg,rgba(255,255,255,0.02),transparent_35%),radial-gradient(circle_at_top,rgba(34,211,238,0.08),transparent_42%)]" />

          <div className="relative space-y-4">
            <div className="relative overflow-hidden rounded-2xl border border-gray-800 bg-[#060910] p-3 max-h-[calc(100vh-240px)]">
              <div className="flex min-w-max justify-center">
                <MapCanvas
                  sector={sector}
                  tiles={tiles}
                  enemies={[]}
                  objects={[]}
                  cities={cities}
                  events={mapEvents}
                  portals={portals}
                  tilesetConfig={tilesetConfig}
                  editorMode="game"
                  viewport={viewport}
                  renderSizeOverride={renderTileSize}
                  isDrawing={false}
                  showCities
                  showEnemies={false}
                  showObjects={false}
                  showEvents={false}
                  showPortals
                  showBotMarker
                  showGrid
                  movementPreview={jumpMode ? jumpPreview : movementPreview}
                  testBotCoordinate={visualCoordinate}
                  onClickTile={handleMapCanvasClick}
                  botClanId={robot?.clan}
                />
              </div>
              <div className="pointer-events-none absolute inset-0 z-10">
                <div className={`pointer-events-auto absolute left-3 bottom-3 ${showRadarPanel ? 'w-[240px] sm:w-[260px] lg:w-[290px]' : 'w-fit'}`}>
                <RadarPanel
                  bot={robot}
                  items={activeAccessoryRows}
                  enemies={enemies}
                  cities={cities}
                  objects={objects}
                  missionTarget={missionRadarTarget}
                  width={width}
                  height={height}
                  compact
                  open={showRadarPanel}
                    onToggle={() => setShowRadarPanel((value) => !value)}
                  />
                </div>

                <div className={`pointer-events-auto absolute right-3 bottom-3 ${showDamagePanel ? 'w-[250px] sm:w-[270px] lg:w-[300px]' : 'w-fit'}`}>
                  <BotDamagePanel
                    bot={robot}
                    title="Danio del BOT"
                    compact
                    open={showDamagePanel}
                    onToggle={() => setShowDamagePanel((value) => !value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {cityPrompt && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-3xl border border-cyan-400/30 bg-[#08111f] shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-cyan-400/20">
              <p className="text-xs uppercase tracking-[0.25em] text-cyan-300/70">Acceso urbano</p>
              <h3 className="text-2xl font-bold text-white mt-1">{cityPrompt.city.name}</h3>
              <p className="text-sm text-slate-300 mt-2">
                Tipo: {cityTypeLabel} | Clan: {cityClanLabel}
              </p>
            </div>

            <div className="p-5 space-y-3 text-sm text-slate-300">
              <p className={canEnterCity ? 'text-slate-300' : 'text-rose-300'}>{cityMessage}</p>
              {cityHackRequired && (
                <p className="text-cyan-200">
                  Abre la terminal EW, usa <span className="font-bold">city bypass</span> y vuelve a este modal para confirmar la entrada.
                </p>
              )}
              {canEnterCity && needsFee && !cityHackRequired && (
                <p className="text-amber-300">
                  Peaje de entrada: <span className="font-bold">${entryFeeAmount}</span>. Al confirmar, se descontara automaticamente.
                </p>
              )}
              {canEnterCity && !needsFee && !cityHackRequired && (
                <p className="text-emerald-300">No necesitas pagar peaje para entrar.</p>
              )}
            </div>

            <div className="px-5 pb-5 flex gap-3">
              {cityHackRequired ? (
                <>
                  <button
                    type="button"
                    onClick={() => void openTerminalModal()}
                    className="flex-1 rounded-xl border border-emerald-500 bg-emerald-600 px-4 py-3 text-white font-semibold hover:bg-emerald-500"
                  >
                    Abrir terminal
                  </button>
                  <button
                    type="button"
                    onClick={() => void cancelCityEntry({ persist: true })}
                    className="rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-slate-100 hover:bg-slate-700"
                  >
                    Cerrar
                  </button>
                </>
              ) : canEnterCity ? (
                <>
                  <button
                    type="button"
                    onClick={() => void cancelCityEntry()}
                    className="flex-1 rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-slate-100 hover:bg-slate-700"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={!canEnterCity}
                    onClick={() => void confirmCityEntry()}
                    className="flex-1 rounded-xl border border-cyan-500 bg-cyan-600 px-4 py-3 text-white font-semibold hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {needsFee ? `Pagar $${entryFeeAmount} y entrar` : 'Entrar'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => void cancelCityEntry({ persist: false })}
                  className="flex-1 rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-slate-100 hover:bg-slate-700"
                >
                  Cerrar
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {missionPrompt && (
        <div className="fixed inset-0 z-40 pointer-events-none flex items-start justify-center p-4 pt-20">
          <div className="pointer-events-auto w-full max-w-2xl rounded-3xl border border-amber-300/40 bg-[#120f06]/95 shadow-[0_0_50px_rgba(251,191,36,0.2)] overflow-hidden">
            <div className="px-5 py-4 border-b border-amber-300/20">
              <p className="text-xs uppercase tracking-[0.3em] text-amber-200/70">
                {missionPrompt.missionCompleted ? 'Mision completada' : 'Progreso de mision'}
              </p>
              <h3 className="mt-1 text-2xl font-bold text-amber-50">{missionPrompt.missionName || 'Evento de mision'}</h3>
              <p className="mt-2 text-sm text-slate-300">
                Fase {missionPrompt.phaseNumber || 1} de {missionPrompt.totalPhases || 1}
              </p>
            </div>

            <div className="space-y-3 px-5 py-5 text-sm text-slate-200">
              {missionPrompt.eventText && (
                <p>{missionPrompt.eventText}</p>
              )}
              {Array.isArray(missionPrompt.summary) && missionPrompt.summary.length > 0 && (
                <p className="text-amber-100">{missionPrompt.summary.join(' | ')}</p>
              )}
              {(Number(missionPrompt.moneyDelta || 0) !== 0 || Number(missionPrompt.experienceDelta || 0) > 0) && (
                <p className="text-emerald-300">
                  {Number(missionPrompt.moneyDelta || 0) !== 0 ? `Dinero: ${missionPrompt.moneyDelta > 0 ? '+' : ''}$${missionPrompt.moneyDelta}` : 'Sin cambio de dinero'}
                  {Number(missionPrompt.experienceDelta || 0) > 0 ? ` | XP: +${missionPrompt.experienceDelta}` : ''}
                </p>
              )}
              {missionPrompt.levelUp && (
                <p className="text-cyan-300">Tu bot ha subido de nivel con esta mision.</p>
              )}
              {missionPrompt.nextPhaseNumber && !missionPrompt.missionCompleted && (
                <p className="text-slate-300">Siguiente objetivo: fase {missionPrompt.nextPhaseNumber}.</p>
              )}
            </div>

            <div className="px-5 pb-5 flex justify-end">
              <button
                type="button"
                onClick={closeMissionPrompt}
                className="rounded-xl border border-amber-300/40 bg-amber-500/15 px-4 py-2 font-semibold text-amber-100 hover:bg-amber-500/25"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {cityInfoPrompt && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-3xl border border-cyan-400/30 bg-[#08111f] shadow-2xl overflow-hidden">
            <div className={`px-5 py-4 border-b ${cityInfoPrompt.isOwnClan ? 'border-emerald-400/30' : 'border-rose-400/30'}`}>
              <p className={`text-xs uppercase tracking-[0.25em] ${cityInfoPrompt.isOwnClan ? 'text-emerald-300/70' : 'text-rose-300/70'}`}>
                Centro urbano
              </p>
              <h3 className="text-2xl font-bold text-white mt-1">{cityInfoPrompt.city.name}</h3>
              <p className="text-sm text-slate-300 mt-2">
                Clan: {cityInfoPrompt.city.clan_name || (Number(cityInfoPrompt.city.clan) === 0 ? 'Neutral' : `Clan #${cityInfoPrompt.city.clan ?? '-'}`)}
              </p>
            </div>

            <div className="p-5 space-y-4 text-sm text-slate-300">
              <div className={`rounded-2xl border p-4 ${cityInfoPrompt.isOwnClan ? 'border-emerald-400/30 bg-emerald-500/10' : 'border-rose-400/30 bg-rose-500/10'}`}>
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-lg border-2 ${cityInfoPrompt.isOwnClan ? 'border-emerald-400 bg-emerald-500/25' : 'border-rose-400 bg-rose-500/25'}`} />
                  <div>
                    <p className={`font-semibold ${cityInfoPrompt.isOwnClan ? 'text-emerald-200' : 'text-rose-200'}`}>
                      {cityInfoPrompt.isOwnClan ? 'Ciudad de tu clan' : 'Ciudad de otro clan'}
                    </p>
                    <p className="text-slate-300/90">
                      Coordenada: {cityInfoPrompt.coordinate}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-5 pb-5 flex justify-end">
              <button
                type="button"
                onClick={() => closeCityInfoPrompt()}
                className="rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-slate-100 hover:bg-slate-700"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {objectPrompt && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-3xl border border-cyan-400/30 bg-[#08111f] shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-cyan-400/20">
              <p className="text-xs uppercase tracking-[0.25em] text-cyan-300/70">
                {objectPrompt.kind === 'enemy'
                  ? 'Amenaza detectada'
                  : Number(selectedObject?.item_type || objectPrompt.object?.item_type || 0) === 7
                    ? 'Amenaza detectada'
                    : 'Objeto detectado'}
              </p>
              <h3 className="text-2xl font-bold text-white mt-1">
                {objectPrompt.kind === 'enemy'
                  ? (objectPrompt.object?.name || 'Elemento del mapa')
                  : (selectedObject?.name || objectPrompt.object?.name || 'Elemento del mapa')}
              </h3>
                <p className="text-sm text-slate-300 mt-2">
                  Coordenada: {objectPrompt.coordinate}
                  {objectPrompt.kind !== 'enemy' && (
                  <> | {selectedObjectIsBuried ? 'Enterrado' : 'En superficie'}</>
                )}
              </p>
            </div>

            <div className="p-5 space-y-3 text-sm text-slate-300">
              <p className={objectPrompt.kind === 'enemy' || Number(objectPrompt.object?.item_type || 0) === 7 ? 'text-amber-300' : 'text-slate-200'}>
                {objectPrompt.kind === 'enemy'
                  ? (objectPrompt.canFight
                    ? 'Hay un enemigo bloqueando esa casilla. Puedes iniciar combate o huir.'
                    : 'Hay un enemigo bloqueando esa casilla, pero necesitas un radar equipado para luchar.')
                  : selectedObject?.description || objectPrompt.object?.description || (
                    Number(selectedObject?.item_type || objectPrompt.object?.item_type || 0) === 7
                      ? 'Hay una mina en esta casilla. Puedes desactivarla si estas junto a ella.'
                      : 'Tu radar ha detectado este objeto en el mapa.'
                  )}
              </p>
              {objectPrompt.kind !== 'enemy' && objectPromptObjects.length > 1 && (
                <div className="rounded-2xl border border-slate-600 bg-slate-900/70 p-3">
                  <label className="block text-xs uppercase tracking-[0.2em] text-cyan-300/70 mb-2">
                    Selecciona el objeto a recoger
                  </label>
                  <select
                    value={selectedObjectId || ''}
                    onChange={(event) => {
                      const nextSelectedId = Number(event.target.value);
                      setObjectPrompt((prev) => {
                        if (!prev) return prev;
                        const nextPrompt = { ...prev, selectedObjectId: nextSelectedId };
                        objectPromptRef.current = nextPrompt;
                        return nextPrompt;
                      });
                    }}
                    className="w-full rounded-xl border border-slate-600 bg-[#0b1324] px-4 py-3 text-slate-100 outline-none"
                  >
                    {objectPromptObjects.map((entry) => (
                      <option key={`${entry.source || 'unknown'}-${entry.id}`} value={entry.id}>
                        {entry.name}
                        {entry.quantity > 1 ? ` x${entry.quantity}` : ''}
                        {entry.source ? ` · ${entry.source === 'ground' ? 'suelo' : entry.source === 'map' ? 'mapa' : entry.source}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {objectPrompt.kind !== 'enemy' && selectedObjectIsBuried && (
                <p className="text-cyan-200">
                  Este objeto requiere un radar con deteccion de enterrados para aparecer y poder recogerlo.
                </p>
              )}
              <div className="flex flex-wrap gap-3 pt-2">
                {objectPrompt.kind === 'enemy' && (
                  <>
                    {objectPrompt.canFight && (
                      <button
                        type="button"
                        disabled={objectActionLoading}
                        onClick={() => void handleStartCombat()}
                        className="flex-1 rounded-xl border border-rose-500 bg-rose-600 px-4 py-3 text-white font-semibold hover:bg-rose-500 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Luchar
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => closeObjectPrompt()}
                      className="rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-slate-100 hover:bg-slate-700"
                    >
                      {objectPrompt.canFight ? 'Huir' : 'Cerrar'}
                    </button>
                  </>
                )}
                {objectPrompt.kind !== 'enemy' && selectedObject && Number(selectedObject.item_type || 0) !== 7 && (
                  <button
                    type="button"
                    disabled={objectActionLoading}
                    onClick={() => void handlePickupObject()}
                    className="flex-1 rounded-xl border border-cyan-500 bg-cyan-600 px-4 py-3 text-white font-semibold hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {selectedObjectIsBuried ? 'Excavar y recoger' : 'Recoger'}
                  </button>
                )}
                {objectPrompt.kind !== 'enemy' && selectedObject && Number(selectedObject.item_type || 0) === 7 && (
                  <button
                    type="button"
                    disabled={objectActionLoading}
                    onClick={() => void handleDisarmMine()}
                    className="flex-1 rounded-xl border border-amber-500 bg-amber-600 px-4 py-3 text-white font-semibold hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Desactivar mina
                  </button>
                )}
                {objectPrompt.kind !== 'enemy' && (
                  <button
                    type="button"
                    onClick={() => closeObjectPrompt()}
                    className="rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-slate-100 hover:bg-slate-700"
                  >
                    Cerrar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {portalPrompt && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-3xl border border-cyan-400/30 bg-[#08111f] shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-cyan-400/20">
              <p className="text-xs uppercase tracking-[0.25em] text-cyan-300/70">Portal detectado</p>
              <h3 className="text-2xl font-bold text-white mt-1">Transporte entre sectores</h3>
              <p className="text-sm text-slate-300 mt-2">
                Coordenada: {portalPrompt.coordinate} | Destino: {portalPrompt.destinationSectorName}
              </p>
            </div>

            <div className="p-5 space-y-3 text-sm text-slate-300">
              <p className="text-cyan-100">
                Este portal te lleva a {portalPrompt.destinationSectorName}. Si lo activas, tu bot aparecerá en la coordenada {portalPrompt.destinationCoordinate}.
              </p>
            </div>

            <div className="px-5 pb-5 flex gap-3">
              <button
                type="button"
                disabled={portalActionLoading}
                onClick={() => closePortalPrompt()}
                className="flex-1 rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-slate-100 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={portalActionLoading}
                onClick={() => void handleUsePortal()}
                className="flex-1 rounded-xl border border-cyan-500 bg-cyan-600 px-4 py-3 text-white font-semibold hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {portalActionLoading ? 'Viajando...' : 'Ir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTerminalModal && (
        <ElectronicWarfareTerminalModal
          open={showTerminalModal}
          onClose={closeTerminalModal}
          botName={robot?.name || 'BOT'}
          moduleRow={ewSource}
          cityHackTarget={cityPrompt?.access?.requiresTerminalBypass ? cityPrompt : null}
          onCityBypassAuthorized={handleCityBypassAuthorized}
        />
      )}

      {showInventoryModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-7xl max-h-[92vh] rounded-3xl border border-cyan-400/30 bg-[#08111f] shadow-2xl overflow-hidden">
            <div className="max-h-[92vh] overflow-y-auto">
              <BotInventory embedded onClose={closeInventoryModal} onInventoryUpdated={loadGameState} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
